import type { NotificationMarker } from '../../common/types';

/**
 * Область уведомлений — раздел приложения, который показывает свои непрочитанные
 * счётчиком на пункте меню.
 *
 * Пара к правилу дедупликации на сервере: там модуль решает, что считать одним
 * и тем же уведомлением, здесь — какие уведомления «его». Платформа обе стороны
 * не интерпретирует.
 *
 * **Ключ группы должен приходить из общего места** (`common/` модуля), а не
 * набираться строкой отдельно на сервере и отдельно здесь: разъехавшись, они
 * молча перестанут совпадать, и счётчик обнулится без единой ошибки.
 */
export interface NotificationScope {
  /** Код области — им её спрашивают компоненты (`useNotificationScope`) */
  code: string;

  /**
   * Относится ли маркер к этой области.
   *
   * Обычно — префикс `dedup_key`; для уведомлений без ключа годятся
   * `entity_type` или `entity_url`.
   */
  match(marker: NotificationMarker): boolean;
}

/**
 * Реестр областей.
 *
 * Upsert по `code` — безопасно для dev/HMR. Пустой реестр это рабочее
 * состояние: без областей уведомления живут только в общей ленте.
 */
class NotificationScopeRegistry {
  private static instance: NotificationScopeRegistry | null = null;

  private readonly scopesByCode = new Map<string, NotificationScope>();

  static getInstance(): NotificationScopeRegistry {
    if (!NotificationScopeRegistry.instance) {
      NotificationScopeRegistry.instance = new NotificationScopeRegistry();
    }
    return NotificationScopeRegistry.instance;
  }

  register(scope: NotificationScope): void {
    this.scopesByCode.set(scope.code, scope);
  }

  get(code: string): NotificationScope | null {
    return this.scopesByCode.get(code) ?? null;
  }

  getAll(): NotificationScope[] {
    return Array.from(this.scopesByCode.values());
  }
}

const registry = NotificationScopeRegistry.getInstance();

/**
 * Зарегистрировать область уведомлений (upsert по `code`).
 *
 * Вызывается из `client/bootstrap.ts` модуля или инстанса — до того, как
 * отрисуется пункт меню с бейджем.
 */
export function registerNotificationScope(scope: NotificationScope): NotificationScope {
  registry.register(scope);
  return scope;
}

/** Область по коду; `null` — такой не регистрировали */
export function getNotificationScope(code: string): NotificationScope | null {
  return registry.get(code);
}

/** Все зарегистрированные области */
export function getNotificationScopes(): NotificationScope[] {
  return registry.getAll();
}

/**
 * Отобрать маркеры области.
 *
 * Незарегистрированная область — пустой список, а не исключение: бейдж
 * не должен ронять меню из-за опечатки в коде области.
 */
export function selectScopeMarkers(
  code: string,
  markers: NotificationMarker[]
): NotificationMarker[] {
  const scope = registry.get(code);
  if (!scope) {
    return [];
  }

  return markers.filter(marker => {
    try {
      return scope.match(marker);
    } catch {
      return false; // упавший матчер не должен обнулять чужие области
    }
  });
}
